import React, {useState, useMemo} from "react";
import {Row} from "antd";
import moment from "moment";
import ShiftManagementActions from "./Components/ShiftManagementActions";
import ShiftManagementFilters from "./Components/ShiftManagementFilters";
import ShiftManagementTable from "./Components/ShiftManagementTable";
import {normalizeShiftRows} from "./Components/normalizeShiftRow";
import {isPoolWorkType, SHIFT_WORK_TYPES} from "../../constants/shiftWorkTypes";
import {useFetch} from "../../hooks/useFetch";
import {useStore} from "../../hooks/useStore";

const PAGE_SIZE = 10;
// Havuz ve dedike entegrasyon listeleri tek istekte çekilip sayfalama istemcide yapılıyor
// (Operasyon Yönetimi ile aynı yaklaşım); sayfa değiştirmek yeni istek üretmiyor. Sabit (düz
// dedike) liste bunun istisnası: sunucuda sayfalanır (bkz. `getShiftList` çağrısı).
const FETCH_LIMIT = 200;

const ShiftManagement = () => {
    const stores = useStore();
    const [currentPage, setCurrentPage] = useState(1);
    const [reloadData, setReloadData] = useState({});
    const [workType, setWorkType] = useState(
        stores.meStore.map.filters.workType || SHIFT_WORK_TYPES.DEDICATED
    );
    const [filters, setFilters] = useState({
        city: "all",
        company: undefined,
        subcompany: [],
        pool: [],
        status: "all",
        period: "choose",
        startDate: moment()
            .subtract(1, "d")
            .toDate(),
        endDate: moment().toDate(),
        date: moment().toDate(),
        searchText: undefined
    });

    const isPoolShift = isPoolWorkType(workType);
    const isPool = workType === SHIFT_WORK_TYPES.POOL;
    // Sabit (düz dedike) liste sunucuda sayfalanır ve durum filtresi de sunucuya iletilir;
    // havuz ile dedike entegrasyonda sayfalama ve durum filtresi istemcide uygulanır.
    const isServerPaginated = !isPoolShift;

    // Havuz/entegrasyon tarafında durum filtresi istemcide uygulandığı için (bkz. aşağıdaki
    // `rows`) fetch bağımlılıklarına girmez; sabit tarafta sayfa ve durum ayrı bir anahtarla
    // (`dedicatedFetchKey`) izlenir.
    const fetchFiltersKey = JSON.stringify({
        city: filters.city,
        company: filters.company,
        subcompany: filters.subcompany,
        pool: filters.pool,
        period: filters.period,
        startDate: filters.startDate,
        endDate: filters.endDate,
        date: filters.date,
        searchText: filters.searchText
    });

    // Sabit listede sayfa veya durum değişimi yeni istek üretir; diğer tiplerde anahtar boş
    // kalır, istek yalnızca filtre değişince tetiklenir.
    const dedicatedFetchKey = isServerPaginated
        ? JSON.stringify({page: currentPage, status: filters.status})
        : "";

    const [loading, data] = useFetch({
        fetchFn: () => {
            if (isPoolShift) {
                return stores.poolShiftStore.getCourierPoolShifts({
                    // Havuz ucu tek poolId kabul ediyor; çoklu seçimde tümü çekilip aşağıda
                    // istemcide daraltılıyor. Sabit entegrede havuz filtresi yok, daraltma
                    // firma/şube üzerinden yapılıyor.
                    poolId: isPool && filters.pool?.length === 1 ? filters.pool[0] : "all",
                    integrationType: workType,
                    companyId: filters.company,
                    // Havuz ucu tek alt firma kabul ediyor; çoklu seçimde daraltma yapılmaz ki
                    // yanlış tek bir şube gönderilmesin (Excel dışa aktarımıyla aynı kural).
                    subCompanyId:
                        filters.subcompany?.length === 1 ? filters.subcompany[0] : undefined,
                    date: moment(filters.date).format("YYYY-MM-DD"),
                    searchText: filters.searchText,
                    city: filters.city
                });
            }
            return stores.shiftStore.getShiftList({
                limit: PAGE_SIZE,
                skip: (currentPage - 1) * PAGE_SIZE,
                city: filters.city,
                company: filters.company,
                // Vardiya listesi ucu da tek alt firma kabul ediyor.
                subcompany: filters.subcompany?.length === 1 ? filters.subcompany[0] : undefined,
                period: filters.period,
                startDate: filters.startDate,
                endDate: filters.endDate,
                searchText: filters.searchText,
                // Sabit vardiyada durum filtresi sunucuda uygulanır: liste sunucuda
                // sayfalandığı için istemcide daraltmak eksik sonuç verirdi (bkz. `rows`).
                status: filters.status
            });
        },
        initialCall: true,
        dependencies: [fetchFiltersKey, workType, reloadData, dedicatedFetchKey]
    });

    // Havuz/entegrasyon listesi tek istekte geldiği için havuz çoklu seçimi ve durum filtresi
    // normalize edilmiş etiket üzerinden istemcide uygulanır. Sabit liste sunucuda sayfalanıp
    // durumla filtrelendiği için satırlar olduğu gibi basılır.
    const rows = useMemo(() => {
        const normalized = normalizeShiftRows(isPoolShift ? data?.data : data?.rows, workType);
        if (!isPoolShift) return normalized;
        let filtered = normalized;
        if (isPool && filters.pool?.length > 1) {
            filtered = filtered.filter(row => filters.pool.includes(row.raw?.shiftId));
        }
        if (filters.status === "all") return filtered;
        return filtered.filter(row => row.status === filters.status);
    }, [data, isPoolShift, isPool, workType, filters.status, filters.pool]);

    return (
        <div>
            <Row gutter={[8, 8]}>
                <ShiftManagementFilters
                    setCurrentPage={setCurrentPage}
                    filters={filters}
                    setFilters={setFilters}
                    workType={workType}
                    setWorkType={setWorkType}
                />
            </Row>
            <Row>
                <ShiftManagementActions
                    filters={filters}
                    skip={0}
                    limit={FETCH_LIMIT}
                    setReloadData={() => setReloadData(!reloadData)}
                    workType={workType}
                />
            </Row>
            <Row style={{marginTop: 16}}>
                <ShiftManagementTable
                    loading={loading}
                    data={rows}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    // Sabit listede toplam satır sayısı sunucudan gelir; havuz/entegrasyonda
                    // tanımsız bırakılıp antd satır sayısından sayfalar.
                    dataCount={isServerPaginated ? data?.dataCount : undefined}
                    setReloadData={() => setReloadData(!reloadData)}
                    workType={workType}
                />
            </Row>
        </div>
    );
};

export default ShiftManagement;
